import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { VehicleSearchService } from '../../../services/vehicle/vehicle.service';
import { ActivatedRoute } from '@angular/router';
import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import { skip, Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-vehicle-search-result',
  imports: [CommonModule, RouterModule, FormsModule, HeaderComponent, SidebarComponent],
  templateUrl: './vehicle-search-result.component.html',
  styleUrl: './vehicle-search-result.component.css',
})
export class VehicleSearchResultComponent implements OnInit, OnDestroy {
  searchTerm: string = '';
  searchResultDetails: any[] = [];
  searchResultDetailsFiltered: any[] = [];
  vehicleDetails: any;
  noResultsFound: boolean = false;
  notes: string = '';

  loadingCompletedSearchResult: boolean = false;
  loadingPolledSearchResult: boolean = false;
  jobResultSubscription?: Subscription;

  selectedSearchResult: any;
  jobId: string = '';
  searchId: string = '';

  get isLoadingResults(): boolean {
    const hasResults = !!this.vehicleDetails;
    return (
      (this.loadingCompletedSearchResult || this.loadingPolledSearchResult) &&
      !hasResults
    );
  }

  constructor(
    private vehicleSearchService: VehicleSearchService,
    private route: ActivatedRoute,
    private router: Router,
    private searchPollingService: SearchPollingService,
    private snackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    this.selectedSearchResult = history.state?.searchResult;
    this.jobId = this.route.snapshot.paramMap.get('id') || '';

    if (!this.selectedSearchResult && !this.jobId) {
      this.router.navigate(['/vehicle-search']);
      return;
    }

    if (this.selectedSearchResult) {
      this.searchId = this.selectedSearchResult.vehicle_search_id;

      if (this.selectedSearchResult.done) {
        this.getCompletedSearchResult();
      } else {
        this.pollForSearchResult();
      }
    } else if (this.jobId) {
      this.pollForSearchResult();
    }
  }

  ngOnDestroy() {
    this.jobResultSubscription?.unsubscribe();
  }

  getCompletedSearchResult() {
    this.loadingCompletedSearchResult = true;
    this.noResultsFound = false;
    this.vehicleDetails = null;

    this.vehicleSearchService
      .getCompletedSearchResult(this.searchId)
      .subscribe({
        next: (response: any) => {
          // Check if no results found
          if (
            response?.message_key === 'No Search Results Found' ||
            !response?.data?.data_list ||
            response?.data?.data_list?.length === 0
          ) {
            this.noResultsFound = true;
            this.vehicleDetails = null;
          } else if (response?.data?.data_list?.length > 0) {
            this.vehicleDetails = response.data.data_list[0];
            this.searchId = response.data.data_list[0].vehicle_search_id;
            this.noResultsFound = false;

            // Update selectedSearchResult with the fetched data
            this.selectedSearchResult = {
              ...this.selectedSearchResult,
              ...this.vehicleDetails,
            };

            // Initialize notes from response data (like PAN component)
            this.notes =
              response?.data?.notes ||
              this.vehicleDetails?.notes ||
              this.selectedSearchResult?.notes ||
              '';
          }

          this.loadingCompletedSearchResult = false;
          this.loadingPolledSearchResult = false;
        },
        error: (error) => {
          console.error(error);
          this.loadingCompletedSearchResult = false;
          this.loadingPolledSearchResult = false;
        },
      });
  }

  pollForSearchResult() {
    if (!this.jobId) {
      this.jobId = this.selectedSearchResult?.job_id;
    }

    if (!this.jobId) {
      console.error('No job ID available for polling');
      return;
    }

    this.jobResultSubscription?.unsubscribe();
    this.loadingPolledSearchResult = true;

    this.jobResultSubscription = this.searchPollingService
      .getJobResult(this.jobId)
      .pipe(skip(1))
      .subscribe({
        next: (response: any) => {
          if (response) {
            if (response.done === true || response.status === 'completed') {
              this.getCompletedSearchResult();
            } else {
              if (this.selectedSearchResult) {
                this.selectedSearchResult = {
                  ...this.selectedSearchResult,
                  done: response.done || false,
                  status: response.status || 'pending',
                };
              }
            }
          }
        },
        error: (error) => {
          console.error(error);
          this.loadingPolledSearchResult = false;
        },
      });
  }

  onSearchTermChange() {
    const searchTermLower = this.searchTerm.toLowerCase();

    if (!searchTermLower) {
      this.searchResultDetailsFiltered = JSON.parse(
        JSON.stringify(this.searchResultDetails),
      );
      return;
    }
  }

  onBackButtonClick() {
    window.history.back();
  }

  onUpdateNotesButtonClick(): void {
    const resultId = this.selectedSearchResult?.result_id;

    if (!this.notes.trim() || !resultId) {
      this.snackBar.open(
        'Please enter notes and ensure result ID is available.',
        'Close',
        {
          duration: 3000,
          panelClass: ['snackbar-error'],
        },
      );
      return;
    }

    const payload = {
      result_id: resultId,
      notes: this.notes.trim(),
    };

    this.vehicleSearchService.updateVehicleNotes(payload).subscribe({
      next: () => {
        this.snackBar.open('Notes updated successfully!', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success'],
        });
      },
      error: () => {
        this.snackBar.open('Failed to update notes.', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error'],
        });
      },
    });
  }
}
